import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  Upload, FileText, X, Send, RotateCcw, ChevronRight,
  AlertCircle, CheckCircle2, Loader2, FileSpreadsheet, FileType2,
  Bot, Cpu, Search, GitMerge, BarChart2, Mail,
  Star, Paperclip, Zap, Link2, ArrowRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Separator } from '../components/ui/separator';
import { ScrollArea } from '../components/ui/scroll-area';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const ACCEPTED = ['.csv', '.xlsx', '.xls', '.pdf'];
const MAX_FILE_SIZE = 10 * 1024 * 1024;

function getFileIcon(name) {
  const lower = name.toLowerCase();
  if (lower.endsWith('.pdf')) return <FileType2 className="w-3.5 h-3.5 text-red-500" />;
  if (lower.endsWith('.csv')) return <FileText className="w-3.5 h-3.5 text-emerald-600" />;
  return <FileSpreadsheet className="w-3.5 h-3.5 text-blue-500" />;
}

const ACTION_CONFIG = {
  'pre-process': { icon: Cpu,      color: 'text-slate-600',   bg: 'bg-slate-50 border-slate-200' },
  Reasoning:     { icon: Bot,      color: 'text-violet-700',  bg: 'bg-violet-50 border-violet-200', mono: true },
  Thinking:      { icon: Bot,      color: 'text-slate-500',   bg: 'bg-white border-border' },
  extract_from_email:       { icon: Search,    color: 'text-blue-700',    bg: 'bg-blue-50 border-blue-200' },
  cross_reference_sources:  { icon: GitMerge,  color: 'text-orange-700',  bg: 'bg-orange-50 border-orange-200' },
  run_audit_rules:          { icon: BarChart2, color: 'text-emerald-700', bg: 'bg-emerald-50 border-emerald-200' },
  draft_audit_email:        { icon: Mail,      color: 'text-primary',     bg: 'bg-accent border-primary/20' },
};

function getCfg(action) {
  if (action.startsWith('[Reasoning]')) return ACTION_CONFIG.Reasoning;
  if (action.startsWith('[Thinking]'))  return ACTION_CONFIG.Thinking;
  const m = action.match(/^\[([\w-]+)\]/);
  return ACTION_CONFIG[m?.[1]] || ACTION_CONFIG['pre-process'];
}

function LiveEntry({ action }) {
  const cfg = getCfg(action);
  const Icon = cfg.icon;
  const text = action.replace(/^\[[\w-]+\]\s*/, '').slice(0, 180);
  return (
    <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.16 }}
      className={`flex items-start gap-2 p-2 rounded-lg border text-xs ${cfg.bg}`}>
      <Icon className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${cfg.color}`} />
      <span className={`${cfg.mono ? 'font-mono' : ''} ${cfg.color}`}>{text}</span>
    </motion.div>
  );
}

function GmailPickerCard({ email, selected, onSelect }) {
  const isForwarded = email.is_forwarded || email.subject?.toLowerCase().startsWith('fwd:');
  return (
    <motion.div whileTap={{ scale: 0.98 }} onClick={onSelect}
      className={`relative rounded-xl border-2 cursor-pointer transition-all duration-150 p-4 ${
        selected ? 'border-primary bg-primary/5 shadow-md' : 'border-border/60 bg-white hover:border-primary/40 hover:shadow-sm'
      }`}>
      {selected && (
        <div className="absolute top-3 right-3 w-5 h-5 rounded-full bg-primary flex items-center justify-center">
          <CheckCircle2 className="w-3.5 h-3.5 text-white" />
        </div>
      )}
      <div className="flex items-start gap-2 pr-6">
        {isForwarded && (
          <span className="mt-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 flex-shrink-0">FWD</span>
        )}
        <p className="font-semibold text-sm truncate">{email.subject || '(no subject)'}</p>
      </div>
      <p className="text-xs text-muted-foreground mt-0.5 truncate">{email.from}</p>
      <p className="text-xs text-muted-foreground mt-1 line-clamp-2 opacity-70">{email.snippet}</p>
      {isForwarded && (
        <div className="mt-2 p-2 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800">
          <span className="font-semibold">⚠ Forwarded email</span> — may have fewer SKUs than the original.
          {email.original_sender_hint && (
            <span className="block mt-0.5 opacity-80 text-[11px]">Original sender: {email.original_sender_hint.slice(0, 70)}</span>
          )}
          <span className="block mt-0.5 opacity-70 text-[11px]">For full data, search Gmail for the original email from the sender.</span>
        </div>
      )}
      {email.attachments?.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {email.attachments.map((att, i) => (
            <span key={i} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-mono bg-blue-50 text-blue-700 border border-blue-100">
              <Paperclip className="w-2.5 h-2.5" />{att.filename.slice(0, 22)}
            </span>
          ))}
        </div>
      )}
      <p className="text-[10px] text-muted-foreground mt-1.5 opacity-60">
        {email.date?.slice(0, 22)}{email.saved_at && ` · Saved ${new Date(email.saved_at).toLocaleDateString()}`}
      </p>
    </motion.div>
  );
}

export default function NewAudit() {
  const navigate = useNavigate();
  const location = useLocation();
  const fileInputRef = useRef(null);
  const liveRef = useRef(null);

  const [mode, setMode] = useState('paste');
  const [emailBody, setEmailBody] = useState('');
  const [files, setFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [gmailStatus, setGmailStatus] = useState(null);
  const [savedEmails, setSaved] = useState([]);
  const [loadingSaved, setLoadSaved] = useState(false);
  const [selectedEmail, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressLabel, setLabel] = useState('');
  const [error, setError] = useState(null);
  const [liveActions, setLiveActs] = useState([]);
  const [seenCount, setSeen] = useState(0);

  useEffect(() => {
    if (location.state?.gmailEmail) {
      setMode('gmail');
      setSelected(location.state.gmailEmail);
    }
    // Auto-tail an audit that was kicked off elsewhere (e.g. Emails page).
    // The job is already running on the server — we just poll it from here.
    if (location.state?.autoStartJobId) {
      const jobId = location.state.autoStartJobId;
      setLoading(true); setError(null); setLiveActs([]); setSeen(0);
      setProgress(12); setLabel('Audit running…');
      pollJob(jobId)
        .catch(err => { setProgress(0); setLabel(''); setError(err.message); })
        .finally(() => setLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  useEffect(() => {
    if (liveRef.current) liveRef.current.scrollTop = liveRef.current.scrollHeight;
  }, [liveActions]);

  useEffect(() => {
    if (mode !== 'gmail') return;
    fetch(`${BACKEND}/api/gmail/status`).then(r => r.json()).then(setGmailStatus);
    setLoadSaved(true);
    fetch(`${BACKEND}/api/gmail/saved`).then(r => r.json()).then(d => setSaved(d.emails || [])).finally(() => setLoadSaved(false));
  }, [mode]);

  const addFiles = useCallback((incoming) => {
    const valid = [];
    for (const f of incoming) {
      const ext = '.' + f.name.split('.').pop().toLowerCase();
      if (!ACCEPTED.includes(ext)) { toast.error(`Unsupported: ${f.name}`); continue; }
      if (f.size > MAX_FILE_SIZE) { toast.error(`Too large: ${f.name}`); continue; }
      valid.push(f);
    }
    if (valid.length) setFiles(prev => { const ex = new Set(prev.map(x => x.name)); return [...prev, ...valid.filter(f => !ex.has(f.name))]; });
  }, []);

  const handleDrop = useCallback((e) => { e.preventDefault(); setDragOver(false); addFiles(Array.from(e.dataTransfer.files)); }, [addFiles]);
  const handleClear = () => { setEmailBody(''); setFiles([]); setError(null); setLiveActs([]); setSeen(0); setSelected(null); };

  const pollJob = async (job_id) => {
    let retries = 0;
    while (retries < 300) {
      await new Promise(r => setTimeout(r, 2000));
      const res = await fetch(`${BACKEND}/api/audit/status/${job_id}`);
      const status = await res.json().catch(() => null);
      if (!res.ok || !status) { retries++; continue; }
      const incoming = status.live_actions || [];
      if (incoming.length > seenCount) {
        setLiveActs(incoming); setSeen(incoming.length);
        const last = incoming[incoming.length - 1] || '';
        if (last.includes('pre-process'))         { setProgress(20); setLabel('Parsing documents...'); }
        else if (last.includes('extract_from'))   { setProgress(40); setLabel('Extracting email data...'); }
        else if (last.includes('cross_reference')){ setProgress(55); setLabel('Cross-referencing...'); }
        else if (last.includes('run_audit'))      { setProgress(70); setLabel('Applying audit rules...'); }
        else if (last.includes('draft_audit'))    { setProgress(85); setLabel('Drafting email report...'); }
        else if (last.includes('Saving'))         { setProgress(95); setLabel('Saving...'); }
      }
      if (status.status === 'complete') { setProgress(100); setLabel('Complete!'); toast.success('Audit complete!'); setTimeout(() => navigate(`/results/${status.audit_id}`), 400); return; }
      if (status.status === 'failed') throw new Error(status.error || 'Audit failed');
      retries++;
    }
    throw new Error('Audit is taking longer than expected (>10 min). Large documents may need more time. Please retry.');
  };

  const handleSubmitPaste = async (e) => {
    e.preventDefault();
    if (!emailBody.trim() && files.length === 0) { toast.error('Add email body or files.'); return; }
    setLoading(true); setError(null); setLiveActs([]); setSeen(0); setProgress(8); setLabel('Submitting...');
    try {
      const formData = new FormData();
      formData.append('email_body', emailBody);
      for (const f of files) formData.append('files', f);
      const res = await fetch(`${BACKEND}/api/audit`, { method: 'POST', body: formData });
      let data;
      try {
        data = await res.json();
      } catch (e) {
        throw new Error('Server error');
      }
      if (!res.ok) throw new Error(data.detail || `Server error ${res.status}`);
      const { job_id } = data;
      setProgress(10); setLabel('Agent starting...');
      await pollJob(job_id);
    } catch (err) { setProgress(0); setLabel(''); setError(err.message); }
    finally { setLoading(false); }
  };

  const handleSubmitGmail = async () => {
    if (!selectedEmail) { toast.error('Select an email first.'); return; }
    setLoading(true); setError(null); setLiveActs([]); setSeen(0); setProgress(8); setLabel('Fetching from Gmail...');
    try {
      const res = await fetch(`${BACKEND}/api/audit/from-gmail`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ saved_email_id: selectedEmail._id }),
      });
      let data;
      try {
        data = await res.json();
      } catch (e) {
        throw new Error('Server error');
      }
      if (!res.ok) throw new Error(data.detail || `Server error ${res.status}`);
      const { job_id } = data;
      setProgress(12); setLabel('Agent starting...');
      await pollJob(job_id);
    } catch (err) { setProgress(0); setLabel(''); setError(err.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="page-pad max-w-[1200px]">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk' }}>New Audit</h1>
        <p className="mt-1 text-muted-foreground text-sm">Paste a reorder email or fetch directly from Gmail.</p>
      </div>

      {/* MODE SWITCHER */}
      <div className="mb-6">
        <div className="inline-flex rounded-2xl border border-border bg-secondary/50 p-1 gap-1">
          {[
            { key: 'paste', icon: <FileText className="w-4 h-4" />, label: 'Write / Paste' },
            { key: 'gmail', icon: <Mail className="w-4 h-4" />, label: 'From Gmail', badge: 'GMAIL' },
          ].map(({ key, icon, label, badge }) => (
            <button key={key} onClick={() => !loading && setMode(key)} disabled={loading}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${
                mode === key ? 'bg-white shadow-sm text-primary border border-border/60' : 'text-muted-foreground hover:text-foreground'
              }`}>
              {icon}{label}
              {badge && <span className="ml-0.5 px-1.5 py-0.5 text-[10px] rounded-full bg-[#4285F4]/10 text-[#4285F4] font-bold">{badge}</span>}
            </button>
          ))}
        </div>
      </div>

      {/* PASTE MODE */}
      <div style={{ display: mode === 'paste' ? 'block' : 'none' }}>
        <form onSubmit={handleSubmitPaste}>
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-5">
            <div className="space-y-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Email Body</CardTitle>
                  <CardDescription className="text-xs">Paste the full reorder request email</CardDescription>
                </CardHeader>
                <CardContent>
                  <Textarea data-testid="new-audit-email-body-textarea"
                    placeholder="Paste reorder request email body…"
                    value={emailBody} onChange={e => setEmailBody(e.target.value)}
                    className="min-h-[200px] font-sans text-sm resize-none" disabled={loading} />
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Attachments</CardTitle>
                  <CardDescription className="text-xs">CSV, Excel (.xlsx/.xls), or PDF</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div data-testid="new-audit-file-dropzone"
                    className={`dropzone${dragOver ? ' drag-over' : files.length > 0 ? ' has-files' : ''}`}
                    onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={handleDrop}
                    onClick={() => !loading && fileInputRef.current?.click()}>
                    <input ref={fileInputRef} type="file" data-testid="new-audit-file-input"
                      multiple accept={ACCEPTED.join(',')} className="sr-only"
                      onChange={e => addFiles(Array.from(e.target.files))} disabled={loading} />
                    <div className="flex flex-col items-center gap-2 pointer-events-none">
                      <Upload className="w-7 h-7 text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium">Drop files or click to browse</p>
                        <p className="text-xs text-muted-foreground mt-0.5">CSV, XLSX, XLS, PDF — max 10MB</p>
                      </div>
                    </div>
                  </div>
                  {files.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {files.map(f => (
                        <span key={f.name} className="file-chip">
                          {getFileIcon(f.name)}
                          <span className="max-w-[180px] truncate">{f.name}</span>
                          {!loading && (
                            <button type="button" className="ml-0.5 text-muted-foreground hover:text-foreground"
                              onClick={ev => { ev.stopPropagation(); setFiles(p => p.filter(x => x.name !== f.name)); }}>
                              <X className="w-3 h-3" />
                            </button>
                          )}
                        </span>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              <ErrorAndLiveFeed {...{ error, loading, progress, progressLabel, liveActions, liveRef }} />

              <div className="flex gap-3">
                <Button type="submit" data-testid="new-audit-submit-button"
                  disabled={loading || (!emailBody.trim() && files.length === 0)}
                  className="h-11 px-6 rounded-xl shadow-sm gap-2">
                  {loading ? <><Loader2 className="w-4 h-4 animate-spin" />Analyzing...</>
                           : <><Send className="w-4 h-4" />Run Audit<ChevronRight className="w-3.5 h-3.5" /></>}
                </Button>
                <Button type="button" variant="ghost" onClick={handleClear} disabled={loading} className="h-11 px-4 gap-1.5">
                  <RotateCcw className="w-4 h-4" />Clear
                </Button>
              </div>
            </div>
            <RightRail />
          </div>
        </form>
      </div>

      {/* GMAIL MODE */}
      <div style={{ display: mode === 'gmail' ? 'block' : 'none' }}>
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-5">
          <div className="space-y-4">
            {gmailStatus && !gmailStatus.connected && (
              <Card className="border-[#4285F4]/20">
                <CardContent className="py-8 text-center">
                  <Mail className="w-10 h-10 text-[#4285F4]/60 mx-auto mb-3" />
                  <p className="font-semibold text-sm mb-1">Gmail not connected</p>
                  <p className="text-xs text-muted-foreground mb-4">Connect your Gmail to fetch emails and attachments directly.</p>
                  <Button onClick={() => navigate('/emails')} className="gap-2" style={{ background: '#4285F4' }}>
                    <Link2 className="w-4 h-4" />Connect Gmail
                  </Button>
                </CardContent>
              </Card>
            )}

            {gmailStatus?.connected && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-200 text-xs">
                  <div className="w-2 h-2 rounded-full bg-emerald-500" />
                  <span className="font-medium text-emerald-800">Connected: {gmailStatus.email}</span>
                  <button className="ml-auto text-emerald-600 hover:text-emerald-800 flex items-center gap-1" onClick={() => navigate('/emails')}>
                    Manage <ArrowRight className="w-3 h-3" />
                  </button>
                </div>

                {loadingSaved && (
                  <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-24 bg-secondary rounded-xl animate-pulse" />)}</div>
                )}

                {!loadingSaved && savedEmails.length === 0 && (
                  <Card className="border-dashed">
                    <CardContent className="py-10 text-center">
                      <Star className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
                      <p className="text-sm font-medium text-muted-foreground">No saved emails</p>
                      <p className="text-xs text-muted-foreground mt-1">Go to Gmail tab to search and save emails for auditing.</p>
                      <Button size="sm" variant="outline" className="mt-4 gap-2" onClick={() => navigate('/emails')}>
                        <Mail className="w-3.5 h-3.5" />Open Gmail
                      </Button>
                    </CardContent>
                  </Card>
                )}

                {!loadingSaved && savedEmails.length > 0 && (
                  <div className="space-y-3">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Select an email to audit ({savedEmails.length} saved)
                    </p>
                    {savedEmails.map(email => (
                      <GmailPickerCard key={email._id} email={email}
                        selected={selectedEmail?._id === email._id}
                        onSelect={() => setSelected(selectedEmail?._id === email._id ? null : email)} />
                    ))}
                  </div>
                )}

                <ErrorAndLiveFeed {...{ error, loading, progress, progressLabel, liveActions, liveRef }} />

                <div className="flex gap-3">
                  <Button onClick={handleSubmitGmail} disabled={loading || !selectedEmail}
                    className="h-11 px-6 rounded-xl shadow-sm gap-2">
                    {loading ? <><Loader2 className="w-4 h-4 animate-spin" />Analyzing...</>
                             : <><Zap className="w-4 h-4" />Audit Selected Email<ChevronRight className="w-3.5 h-3.5" /></>}
                  </Button>
                  {selectedEmail && (
                    <Button variant="ghost" onClick={() => setSelected(null)} disabled={loading} className="h-11 gap-1.5">
                      <RotateCcw className="w-4 h-4" />Deselect
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
          <RightRail />
        </div>
      </div>
    </div>
  );
}

function ErrorAndLiveFeed({ error, loading, progress, progressLabel, liveActions, liveRef }) {
  return (
    <div>
      {error && (
        <Alert variant="destructive" data-testid="audit-error-alert" className="mb-3">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {loading && (
        <div className="space-y-3">
          <Card className="border-primary/20 bg-gradient-to-r from-accent/60 to-white">
            <CardContent className="py-4 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-60" />
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-primary" />
                  </span>
                  <span data-testid="audit-processing-status-text" className="text-sm font-semibold text-primary">
                    {progressLabel || 'Agent running...'}
                  </span>
                </div>
                <span className="text-xs text-muted-foreground tabular-nums">{liveActions.length} action{liveActions.length !== 1 ? 's' : ''}</span>
              </div>
              <Progress data-testid="audit-processing-progress" value={progress} className="h-1.5" />
            </CardContent>
          </Card>
          <Card className="border-border/60">
            <CardHeader className="pb-2 pt-3">
              <CardTitle className="text-xs font-semibold flex items-center gap-2 text-muted-foreground uppercase tracking-wider">
                <Bot className="w-3.5 h-3.5 text-primary" />Live Agent Activity
              </CardTitle>
            </CardHeader>
            <CardContent className="pb-3">
              <div ref={liveRef} className="space-y-1.5 max-h-60 overflow-y-auto pr-1" style={{ scrollBehavior: 'smooth' }}>
                {liveActions.length === 0 ? (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />Waiting for agent...
                  </div>
                ) : liveActions.map((a, i) => <LiveEntry key={i} action={a} />)}
                <motion.div animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1.4, repeat: Infinity }}
                  className="flex items-center gap-1.5 px-2 py-1">
                  {[0, 1, 2].map(i => <span key={i} className="w-1.5 h-1.5 rounded-full bg-primary block" />)}
                </motion.div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

function RightRail() {
  return (
    <div className="space-y-4">
      <Card className="border-border/60">
        <CardHeader className="pb-2"><CardTitle className="text-sm">Checks Applied</CardTitle></CardHeader>
        <CardContent className="space-y-3 text-xs text-muted-foreground">
          {[
            { label: 'WOS Classification', detail: '≤8w OK · >8w Flag · 0 sales + aged >60d = Stagnant' },
            { label: 'Ageing Inventory',   detail: 'Reorder blocked if >10% of total stock is >60 days' },
            { label: 'PI Price Integrity', detail: 'Price Flag if PI > Last Sold' },
          ].map(({ label, detail }) => (
            <div key={label}>
              <p className="font-semibold text-foreground">{label}</p>
              <p className="mt-0.5">{detail}</p>
              <Separator className="mt-2" />
            </div>
          ))}
        </CardContent>
      </Card>
      <Card className="border-border/60">
        <CardHeader className="pb-2"><CardTitle className="text-sm">Supported Formats</CardTitle></CardHeader>
        <CardContent className="space-y-2 text-xs">
          {[
            { icon: <FileText className="w-3.5 h-3.5 text-emerald-600" />, label: 'CSV', desc: 'Inventory tables' },
            { icon: <FileSpreadsheet className="w-3.5 h-3.5 text-blue-500" />, label: 'XLSX / XLS', desc: 'Excel workbooks' },
            { icon: <FileType2 className="w-3.5 h-3.5 text-red-500" />, label: 'PDF', desc: 'Proforma invoices' },
          ].map(({ icon, label, desc }) => (
            <div key={label} className="flex items-center gap-2 text-muted-foreground">
              {icon}<span className="font-mono text-xs text-foreground">{label}</span><span>—</span><span>{desc}</span>
            </div>
          ))}
        </CardContent>
      </Card>
      <Card className="border-border/60">
        <CardHeader className="pb-2"><CardTitle className="text-sm">Recommendations</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {[
            { label: 'ALLOW',      cls: 'bg-emerald-50 text-emerald-800 ring-emerald-200', desc: 'No flags' },
            { label: 'ALERT',      cls: 'bg-orange-50 text-orange-900 ring-orange-200',    desc: 'Price / Ageing flag' },
            { label: 'HIGH ALERT', cls: 'bg-red-50 text-red-800 ring-red-200',             desc: 'Stagnant / High WOS' },
          ].map(({ label, cls, desc }) => (
            <div key={label} className="flex items-center gap-2">
              <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-bold ring-1 ring-inset ${cls}`}>{label}</span>
              <span className="text-xs text-muted-foreground">{desc}</span>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
